import { useCallback } from 'react';

import { handleDecimalOperation, snapValueToStep } from './utils';

// Reference: https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/numberfield/src/useNumberFieldState.ts

interface Params {
  canBeEmpty?: boolean;
  max?: number;
  min?: number;
  onBlur?: (value: number) => void;
  onChange: (value: number) => void;
  step: number;
  value: number;
}

const useNumberInput = (params: Params) => {
  const { canBeEmpty, max, min, onBlur: onBlurProp, onChange: onChangeProp, step, value } = params;

  const safeNextStep = useCallback(
    (operation: '-' | '+', minMax = Number.NaN) => {
      const prev = value;

      if (isNaN(prev)) {
        // if the input is empty, start from the min/max value when incrementing/decrementing,
        // or zero if there is no min/max value defined.
        const newValue = isNaN(minMax) ? 0 : minMax;
        return snapValueToStep(newValue, min, max, step);
      } else {
        // otherwise, first snap the current value to the nearest step. if it moves in the direction
        // we're going, use that value, otherwise add the step and snap that value.
        const newValue = snapValueToStep(prev, min, max, step);
        if ((operation === '+' && newValue > prev) || (operation === '-' && newValue < prev)) {
          return newValue;
        }

        return snapValueToStep(handleDecimalOperation(operation, prev, step), min, max, step);
      }
    },
    [value, min, max, step],
  );

  const onIncrement = useCallback(() => {
    onChangeProp(safeNextStep('+', min));
  }, [min, onChangeProp, safeNextStep]);

  const onDecrement = useCallback(() => {
    onChangeProp(safeNextStep('-', max));
  }, [max, onChangeProp, safeNextStep]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const inputValue = event.target.value;

      if (canBeEmpty && !inputValue.length) {
        onChangeProp(NaN);
        return;
      }

      const newValue = parseInt(event.currentTarget.value, 10);
      onChangeProp(snapValueToStep(isNaN(newValue) ? 0 : newValue, min, max, step));
    },
    [canBeEmpty, max, min, onChangeProp, step],
  );

  const onBlur = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlurProp?.(value);
    },
    [onBlurProp, value],
  );

  return {
    onBlur,
    onChange,
    onDecrement,
    onIncrement,
    value: isNaN(value) ? '' : value.toString(),
  };
};

export default useNumberInput;
